import React, { useEffect, useState } from 'react';
import { getApiUrl } from '../../config';
import { useAuth } from '../../contexts/AuthContext';
import { requestRecipeCompilation } from '../../services/adminService';

type Activity = {
  recipe: {
    active: boolean;
    engineState?: 'agentic' | 'compiling' | 'scripted';
    compilation?: { id: string; status: 'queued' | 'generating' | 'validating' | 'succeeded' | 'failed'; failure: string | null; updatedAt: string } | null;
  };
  webhook: { url: string; received: number; accepted: number; deduplicated: number; rejected: number; succeeded: number; failed: number } | null;
  webhookReceipts?: Array<{ id: string; receivedAt: string; status: string; responseStatus: number | null; executionId: string | null }>;
  repair: { attempts: number; successful: number; unsuccessful: number; inProgress: number };
  schedule: { id: string; description: string; timezone: string; nextOccurrenceAt: string; misfirePolicy: string; overlapPolicy: string } | null;
  occurrences: Array<{ id: string; dueAt: string; windowStartAt: string; windowEndAt: string; status: string; statusMessage: string | null; coalescedIntoId: string | null; createdAt: string }>;
  executions: Array<{ id: string; executionMode: 'agentic' | 'scripted'; triggerSource: string; status: string; retryCount: number; repaired: boolean; message: string | null; createdAt: string; startedAt: string | null; completedAt: string | null; artifacts: Artifact[] }>;
  pagination: {
    executions: { page: number; pageSize: number; total: number };
    occurrences: { page: number; pageSize: number; total: number };
  };
};

type Artifact = { id: string; title: string; fileName: string; mediaType: string; sizeBytes: string; rowCount: number | null; createdAt: string };

type PageInfo = Activity['pagination']['executions'];

const PaginationControls: React.FC<{ pageInfo: PageInfo; onPageChange: (page: number) => void }> = ({ pageInfo, onPageChange }) => {
  const pageCount = Math.max(1, Math.ceil(pageInfo.total / pageInfo.pageSize));
  if (pageInfo.total <= pageInfo.pageSize) return null;

  return (
    <div className="mt-3 flex items-center justify-between text-xs text-gray-500">
      <span>Page {pageInfo.page} of {pageCount} · {pageInfo.total} records</span>
      <div className="flex gap-2">
        <button
          type="button"
          disabled={pageInfo.page <= 1}
          onClick={() => onPageChange(pageInfo.page - 1)}
          className="rounded-md border border-gray-300 bg-white px-2.5 py-1 font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
        >Newer</button>
        <button
          type="button"
          disabled={pageInfo.page >= pageCount}
          onClick={() => onPageChange(pageInfo.page + 1)}
          className="rounded-md border border-gray-300 bg-white px-2.5 py-1 font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
        >Older</button>
      </div>
    </div>
  );
};

const statusClass = (status: string) => {
  if (status === 'succeeded' || status === 'scripted') return 'bg-emerald-100 text-emerald-800';
  if (status === 'rejected' || status === 'partial' || status === 'cancelled') return 'bg-amber-100 text-amber-800';
  if (status === 'failed' || status === 'ambiguous_after_write' || status === 'timed_out') return 'bg-red-100 text-red-800';
  if (status === 'running' || status === 'queued' || status === 'generating' || status === 'validating' || status === 'compiling' || status === 'recovering') return 'bg-blue-100 text-blue-800';
  return 'bg-gray-100 text-gray-700';
};

const executionModeClass = (mode: 'agentic' | 'scripted') => mode === 'scripted'
  ? 'bg-violet-100 text-violet-800 ring-violet-200'
  : 'bg-sky-100 text-sky-800 ring-sky-200';

const formatDuration = (startedAt: string | null, completedAt: string | null) => {
  if (!startedAt) return '—';
  const elapsedMs = Math.max(0, (completedAt ? new Date(completedAt).getTime() : Date.now()) - new Date(startedAt).getTime());
  const totalSeconds = Math.floor(elapsedMs / 1000);
  if (totalSeconds < 60) return `${totalSeconds}s`;
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (minutes < 60) return `${minutes}m ${seconds}s`;
  const hours = Math.floor(minutes / 60);
  return `${hours}h ${minutes % 60}m ${seconds}s`;
};

const formatBytes = (raw: string) => {
  const bytes = Number(raw);
  if (!Number.isFinite(bytes) || bytes < 1024) return `${bytes || 0} B`;
  const units = ['KB', 'MB', 'GB'];
  let value = bytes / 1024;
  let unit = units[0];
  for (let index = 1; index < units.length && value >= 1024; index += 1) {
    value /= 1024;
    unit = units[index];
  }
  return `${value >= 10 ? value.toFixed(0) : value.toFixed(1)} ${unit}`;
};

const canViewArtifact = (mediaType: string) => {
  const type = mediaType.split(';', 1)[0].trim().toLowerCase();
  return type === 'application/pdf'
    || type === 'application/json'
    || type === 'text/plain'
    || type === 'text/csv'
    || ['image/png', 'image/jpeg', 'image/gif', 'image/webp'].includes(type);
};

const RecipeActivity: React.FC<{ recipeId?: number }> = ({ recipeId }) => {
  const { token, currentUser } = useAuth();
  const [activity, setActivity] = useState<Activity | null>(null);
  const [error, setError] = useState('');
  const [compilationRequesting, setCompilationRequesting] = useState(false);
  const [compilationError, setCompilationError] = useState('');
  const [webhookCopyState, setWebhookCopyState] = useState<'idle' | 'copied' | 'error'>('idle');
  const [executionPage, setExecutionPage] = useState(1);
  const [occurrencePage, setOccurrencePage] = useState(1);
  const [artifactLoading, setArtifactLoading] = useState<string | null>(null);
  const [artifactError, setArtifactError] = useState('');

  useEffect(() => {
    setExecutionPage(1);
    setOccurrencePage(1);
  }, [recipeId]);

  useEffect(() => {
    if (!recipeId || !token) return;
    let stopped = false;
    const load = async () => {
      try {
        const query = new URLSearchParams({
          executionPage: String(executionPage),
          occurrencePage: String(occurrencePage),
        });
        const response = await fetch(getApiUrl(`recipes/${recipeId}/activity?${query.toString()}`), {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!response.ok) throw new Error(`Activity request failed (${response.status})`);
        const next = await response.json();
        if (!stopped) {
          setActivity(next);
          setError('');
        }
      } catch (loadError) {
        if (!stopped) setError(loadError instanceof Error ? loadError.message : 'Could not load activity');
      }
    };
    void load();
    const poller = setInterval(() => void load(), 5_000);
    return () => { stopped = true; clearInterval(poller); };
  }, [recipeId, token, executionPage, occurrencePage]);

  if (error && !activity) return <div className="flex h-full items-center justify-center text-sm text-red-600">{error}</div>;
  if (!activity) return <div className="flex h-full items-center justify-center text-sm text-gray-500">Loading activity…</div>;

  const compilationBuilding = activity.recipe.engineState === 'compiling';
  const webhookMetrics = activity.webhook
    ? Object.entries(activity.webhook).filter(([label]) => label !== 'url') as Array<[string, number]>
    : [];

  const copyWebhookUrl = async () => {
    if (!activity.webhook?.url) return;
    try {
      await navigator.clipboard.writeText(activity.webhook.url);
      setWebhookCopyState('copied');
      window.setTimeout(() => setWebhookCopyState('idle'), 2_000);
    } catch {
      setWebhookCopyState('error');
      window.setTimeout(() => setWebhookCopyState('idle'), 2_000);
    }
  };

  const compile = async () => {
    if (!recipeId || !token || compilationBuilding || compilationRequesting) return;
    setCompilationRequesting(true);
    setCompilationError('');
    try {
      const body = await requestRecipeCompilation(recipeId);
      setActivity(current => current ? {
        ...current,
        recipe: {
          ...current.recipe,
          engineState: 'compiling',
          compilation: {
            id: body.implementationId,
            status: 'queued',
            failure: null,
            updatedAt: new Date().toISOString(),
          },
        },
      } : current);
    } catch (compileError) {
      setCompilationError(compileError instanceof Error ? compileError.message : 'Could not start compilation');
    } finally {
      setCompilationRequesting(false);
    }
  };

  const openArtifact = async (executionId: string, artifact: Artifact, download: boolean) => {
    if (!recipeId || !token || artifactLoading) return;
    const preview = download ? null : window.open('', '_blank');
    setArtifactLoading(`${artifact.id}:${download ? 'download' : 'view'}`);
    setArtifactError('');
    try {
      const suffix = download ? '?download=1' : '';
      const response = await fetch(getApiUrl(`recipes/${recipeId}/executions/${executionId}/artifacts/${artifact.id}${suffix}`), {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error(`Could not load ${artifact.fileName}`);
      const objectUrl = URL.createObjectURL(await response.blob());
      if (download) {
        const link = document.createElement('a');
        link.href = objectUrl;
        link.download = artifact.fileName;
        document.body.appendChild(link);
        link.click();
        link.remove();
      } else if (preview) {
        preview.location.href = objectUrl;
      } else {
        throw new Error('Your browser blocked the file viewer');
      }
      window.setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
    } catch (openError) {
      preview?.close();
      setArtifactError(openError instanceof Error ? openError.message : 'Could not load file');
    } finally {
      setArtifactLoading(null);
    }
  };

  return (
    <div className="h-full overflow-auto bg-gray-50 p-4 text-sm text-gray-800">
      <div className="grid gap-3 md:grid-cols-2">
        <section className="rounded-lg border bg-white p-3 shadow-sm">
          <div className="mb-2 flex items-center justify-between">
            <h3 className="font-semibold">Recipe</h3>
            <div className="flex items-center gap-2">
              {currentUser?.role === 'admin' && <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${statusClass(activity.recipe.engineState || 'agentic')}`}>{activity.recipe.engineState}</span>}
              <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${statusClass(activity.recipe.active ? 'running' : 'inactive')}`}>{activity.recipe.active ? 'Active' : 'Inactive'}</span>
              {currentUser?.role === 'admin' && activity.recipe.engineState !== 'scripted' && <button
                type="button"
                disabled={compilationBuilding || compilationRequesting}
                onClick={() => void compile()}
                className="rounded-md border border-gray-300 bg-white px-2.5 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
              >{compilationRequesting ? 'Launching…' : compilationBuilding ? 'Compiling…' : 'Compile'}</button>}
            </div>
          </div>
          <div className="text-xs text-gray-500">Recent activity and results appear below.</div>
          {currentUser?.role === 'admin' && compilationBuilding && <div className="mt-2 text-xs font-medium text-blue-700">Codex is compiling a standalone recipe. A run already in progress will finish; new runs wait until compilation succeeds or fails.</div>}
          {currentUser?.role === 'admin' && activity.recipe.compilation?.status === 'failed' && activity.recipe.compilation.failure && <div className="mt-2 break-words text-xs text-red-700">Compilation failed: {activity.recipe.compilation.failure}</div>}
          {currentUser?.role === 'admin' && compilationError && <div className="mt-2 break-words text-xs text-red-700">{compilationError}</div>}
        </section>
        {activity.webhook && (
          <section className="rounded-lg border bg-white p-3 shadow-sm">
            <div className="mb-2 flex items-center justify-between gap-2">
              <h3 className="font-semibold">Webhook lifecycle</h3>
              <button
                type="button"
                onClick={() => void copyWebhookUrl()}
                className={`rounded-full px-2 py-0.5 text-xs font-medium transition-colors ${webhookCopyState === 'copied' ? 'bg-emerald-100 text-emerald-800' : webhookCopyState === 'error' ? 'bg-red-100 text-red-800' : 'bg-blue-100 text-blue-800 hover:bg-blue-200'}`}
                title="Click to copy"
                aria-label="Copy this recipe's webhook URL"
              >
                {webhookCopyState === 'copied' ? 'Copied' : webhookCopyState === 'error' ? 'Copy failed' : 'Webhook URL'}
              </button>
            </div>
            <div className="grid grid-cols-3 gap-2 text-center text-xs">
              {webhookMetrics.map(([label, value]) => <div key={label}><div className="text-base font-semibold">{value}</div><div className="capitalize text-gray-500">{label}</div></div>)}
            </div>
          </section>
        )}
        <section className="rounded-lg border bg-white p-3 shadow-sm md:col-start-2 md:row-start-2">
          <h3 className="mb-2 font-semibold">Repair</h3>
          <div className="grid grid-cols-4 gap-2 text-center text-xs">
            {([
              ['Attempts', activity.repair.attempts],
              ['Successful', activity.repair.successful],
              ['Unsuccessful', activity.repair.unsuccessful],
              ['In progress', activity.repair.inProgress],
            ] as const).map(([label, value]) => <div key={label}><div className="text-base font-semibold">{value}</div><div className="text-gray-500">{label}</div></div>)}
          </div>
        </section>
        {activity.schedule && (
          <section className="rounded-lg border bg-white p-3 shadow-sm md:col-start-1 md:row-start-2">
            <h3 className="mb-2 font-semibold">Schedule</h3>
            <div className="text-sm text-gray-800">{activity.schedule.description}</div>
            <div className="mt-1 text-xs text-gray-500">
              Next run: {new Date(activity.schedule.nextOccurrenceAt).toLocaleString(undefined, { timeZone: activity.schedule.timezone })} · {activity.schedule.timezone}
            </div>
          </section>
        )}
      </div>

      {!!activity.webhookReceipts?.length && <section className="mt-4 rounded-lg border bg-white p-3">
        <h3 className="mb-2 font-semibold">Recent webhook receipts</h3>
        <p className="mb-2 text-xs text-gray-500">Responses to the sender are separate from recipe results. A response does not confirm that the sender accepted it.</p>
        <div className="space-y-2 text-xs">{activity.webhookReceipts.map(receipt => <div key={receipt.id} className="flex flex-wrap gap-2">
          <span className="text-gray-500">{new Date(receipt.receivedAt).toLocaleString()}</span>
          <span>{receipt.status === 'responded' ? 'Answered without a recipe run' : receipt.status}</span>
          {receipt.responseStatus !== null && <span>HTTP {receipt.responseStatus}</span>}
        </div>)}</div>
      </section>}
      <section className="mt-4">
        <h3 className="mb-2 font-semibold">Recent runs</h3>
        <div className="space-y-2">
          {activity.executions.length === 0 && <div className="rounded-lg border bg-white p-3 text-gray-500">No runs yet.</div>}
          {activity.executions.map(run => (
            <div key={run.id} className="rounded-lg border bg-white p-3 shadow-sm">
              <div className="flex flex-wrap items-center gap-2">
                <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ring-1 ring-inset ${executionModeClass(run.executionMode)}`}>
                  {run.executionMode === 'agentic' ? 'Agentic' : 'Scripted'}
                </span>
                <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${statusClass(run.status)}`}>{run.status}</span>
                {run.repaired && <span
                  className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-800 ring-1 ring-inset ring-amber-200"
                  title="This run repaired its scripted implementation while it was running"
                >Repaired</span>}
                <span className="text-xs text-gray-500">{run.triggerSource}{run.retryCount ? ` · retry ${run.retryCount}` : ''} · {new Date(run.createdAt).toLocaleString()} · Duration: {formatDuration(run.startedAt, run.completedAt)}</span>
              </div>
              {run.message && <p className="mt-2 break-words text-xs text-gray-700">{run.message}</p>}
              {run.artifacts.length > 0 && <div className="mt-3 space-y-2 border-t pt-3">
                {run.artifacts.map(artifact => <div key={artifact.id} className="flex flex-wrap items-center justify-between gap-2 rounded-md bg-gray-50 px-3 py-2">
                  <div className="min-w-0">
                    <div className="truncate text-xs font-semibold text-gray-800">{artifact.title}</div>
                    <div className="truncate text-[11px] text-gray-500">{artifact.fileName} · {formatBytes(artifact.sizeBytes)}{artifact.rowCount !== null ? ` · ${artifact.rowCount.toLocaleString()} rows` : ''}</div>
                  </div>
                  <div className="flex gap-2">
                    {canViewArtifact(artifact.mediaType) && <button type="button" disabled={artifactLoading !== null} onClick={() => void openArtifact(run.id, artifact, false)} className="rounded-md border border-gray-300 bg-white px-2.5 py-1 text-xs font-medium text-gray-700 hover:bg-gray-100 disabled:opacity-50">
                      {artifactLoading === `${artifact.id}:view` ? 'Opening…' : 'View'}
                    </button>}
                    <button type="button" disabled={artifactLoading !== null} onClick={() => void openArtifact(run.id, artifact, true)} className="rounded-md bg-gray-900 px-2.5 py-1 text-xs font-medium text-white hover:bg-gray-700 disabled:opacity-50">
                      {artifactLoading === `${artifact.id}:download` ? 'Preparing…' : 'Download'}
                    </button>
                  </div>
                </div>)}
              </div>}
            </div>
          ))}
        </div>
        {artifactError && <div className="mt-2 text-xs text-red-600">{artifactError}</div>}
        <PaginationControls pageInfo={activity.pagination.executions} onPageChange={setExecutionPage} />
      </section>

      {activity.occurrences.length > 0 && <section className="mt-4">
        <h3 className="mb-2 font-semibold">Schedule occurrences</h3>
        <div className="space-y-2">
          {activity.occurrences.map(occurrence => (
            <div key={occurrence.id} className="flex flex-wrap items-center gap-2 rounded-lg border bg-white p-3 shadow-sm">
              <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${statusClass(occurrence.status)}`}>{occurrence.status === 'cancelled' && occurrence.statusMessage ? 'Skipped' : occurrence.status}</span>
              <span className="text-xs text-gray-500">Due {new Date(occurrence.dueAt).toLocaleString()}</span>
              {occurrence.status === 'coalesced' && <span className="text-xs text-gray-500">Merged into the already queued occurrence</span>}
              {occurrence.statusMessage && <div className="w-full rounded-lg bg-amber-50 px-3 py-2 text-xs leading-5 text-amber-900">{occurrence.statusMessage}</div>}
            </div>
          ))}
        </div>
        <PaginationControls pageInfo={activity.pagination.occurrences} onPageChange={setOccurrencePage} />
      </section>}
    </div>
  );
};

export default RecipeActivity;
